import React from 'react'
import { ImageIcon, ImageOff } from 'lucide-react'
import { PartnerService } from '@duck/api-definitions'
import { Textarea, MultiPick, SinglePick, Switch, cn } from '@duck/ui'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { useTasks } from '@/hooks/useTasks'
import { LanguagePicker, type FeedbackLanguage } from '@/components/LanguagePicker'
import type { ValidationResult } from '@/hooks/useLogoUrlValidation'
import { OrgWordmark } from '@/components/OrgWordmark'

// Inline CSS-only checker pattern — used as a transparency indicator behind logo previews.
const checkerBackgroundStyle: React.CSSProperties = {
    backgroundImage: `
        linear-gradient(45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(-45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #d1d5db 75%),
        linear-gradient(-45deg, transparent 75%, #d1d5db 75%)
    `,
    backgroundSize: '12px 12px',
    backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
    backgroundColor: '#f9fafb',
}

interface LogoUrlFieldProps {
    value: string
    onValueChange: (next: string) => void
    validation: ValidationResult
    labelKey: TranslationKey
    helperKey: TranslationKey
    previewClassName: string
    isSaving: boolean
}

const LogoUrlField: React.FC<LogoUrlFieldProps> = ({
    value,
    onValueChange,
    validation,
    labelKey,
    helperKey,
    previewClassName,
    isSaving,
}) => {
    const { t } = useTar()

    const trimmed = value.trim()
    const hasValue = trimmed !== ''
    const showBrokenPlaceholder = hasValue && validation.status === 'error'
        && validation.messages.includes('common.logoURLErrorFailedToLoad')

    const baseInputClasses = `
      w-full px-3 py-2 text-sm border border-border rounded-md
      bg-card text-foreground placeholder-secondary/60
      focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
      transition-colors hover:border-secondary
    `

    const previewBox = (
        <div
            className={`${previewClassName} rounded-md border border-border overflow-hidden flex items-center justify-center shrink-0`}
            style={checkerBackgroundStyle}
            aria-hidden={!hasValue}
        >
            {hasValue && !showBrokenPlaceholder && (
                <img
                    src={trimmed}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                />
            )}
            {showBrokenPlaceholder && (
                <div className="flex flex-col items-center justify-center text-[10px] leading-tight text-muted-foreground text-center px-1">
                    <ImageOff className="h-4 w-4 mb-0.5" aria-hidden="true" />
                    <span>{t('common.logoURLErrorFailedToLoad')}</span>
                </div>
            )}
            {!hasValue && (
                <ImageIcon
                    className="h-4 w-4 text-muted-foreground/40"
                    aria-hidden="true"
                />
            )}
        </div>
    )

    const showMessages = validation.messages.length > 0
        && (validation.status === 'error' || validation.status === 'warn')

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
                {t(labelKey)}
            </label>
            <div className="flex items-start gap-4">
                <div className="flex-1 space-y-1">
                    <input
                        type="text"
                        autoComplete="off"
                        value={value}
                        onChange={(e) => onValueChange(e.target.value)}
                        disabled={isSaving}
                        className={baseInputClasses}
                        placeholder="https://..."
                    />
                    <p className="text-xs text-muted-foreground">{t(helperKey)}</p>
                    {showMessages && (
                        <ul className="space-y-0.5">
                            {validation.messages.map((key) => (
                                <li
                                    key={key}
                                    className={`text-xs ${validation.status === 'error' ? 'text-error' : 'text-warning'}`}
                                >
                                    {t(key)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {previewBox}
            </div>
        </div>
    )
}

const typeOptions: { value: PartnerService.OrganizationType, label: TranslationKey }[] = [
    { value: 'company', label: 'common.company' },
    { value: 'school', label: 'common.school' },
]

const canvasOptions: { value: PartnerService.OrganizationLogoCanvas, label: TranslationKey }[] = [
    { value: PartnerService.OrganizationLogoCanvas.Plaque, label: 'common.logoCanvasPlaque' },
    { value: PartnerService.OrganizationLogoCanvas.Obsidian, label: 'common.logoCanvasObsidian' },
    { value: PartnerService.OrganizationLogoCanvas.Vellum, label: 'common.logoCanvasVellum' },
]

interface LogoCanvasPickerProps {
    value: PartnerService.OrganizationLogoCanvas
    onChange: (next: PartnerService.OrganizationLogoCanvas) => void
    logoURL: string
    orgName: string
    isSaving: boolean
}

const LogoCanvasPicker: React.FC<LogoCanvasPickerProps> = ({
    value,
    onChange,
    logoURL,
    orgName,
    isSaving,
}) => {
    const { t } = useTar()
    const trimmedLogo = logoURL.trim()
    const hasLogo = trimmedLogo !== ''

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
                {t('common.logoCanvas')}
            </label>
            <p className="text-xs text-muted-foreground">{t('common.logoCanvasHelper')}</p>
            <div className="grid grid-cols-3 gap-3">
                {canvasOptions.map((opt) => {
                    const isSelected = opt.value === value
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            disabled={isSaving}
                            aria-pressed={isSelected}
                            className={cn(
                                'group flex flex-col items-stretch gap-2 rounded-md border p-2 text-start transition',
                                'focus:outline-none focus:ring-2 focus:ring-accent',
                                isSelected
                                    ? 'border-accent ring-2 ring-accent'
                                    : 'border-border hover:border-secondary',
                                isSaving && 'opacity-50 cursor-not-allowed',
                            )}
                        >
                            <div
                                className={cn(
                                    'flex items-center justify-center h-14 rounded-md overflow-hidden',
                                    // The preview tile mimics the card surface so vellum reads correctly.
                                    'bg-card',
                                )}
                            >
                                {hasLogo ? (
                                    <OrgWordmark
                                        org={{ name: orgName || '—', logoURL: trimmedLogo, logoCanvas: opt.value }}
                                        className="max-h-10 max-w-[120px] m-0"
                                    />
                                ) : (
                                    <ImageIcon className="h-5 w-5 text-muted-foreground/40" aria-hidden="true" />
                                )}
                            </div>
                            <span className={cn(
                                'text-xs font-medium text-center',
                                isSelected ? 'text-accent' : 'text-foreground',
                            )}>
                                {t(opt.label)}
                            </span>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export type OrganizationFieldValue =
    | string
    | boolean
    | PartnerService.OrganizationType
    | string[]
    | PartnerService.OrganizationAdminSettings
    | PartnerService.OrganizationSettings

interface SectionProps {
    formData: Partial<PartnerService.Organization>
    onChange: (field: keyof PartnerService.Organization, value: OrganizationFieldValue) => void
    isSaving?: boolean
}

const inputClasses = `
  w-full px-3 py-2 text-sm border border-border rounded-md
  bg-card text-foreground placeholder-secondary/60
  focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
  transition-colors hover:border-secondary
`

const FieldLabel: React.FC<{ label: TranslationKey; required?: boolean }> = ({ label, required }) => {
    const { t } = useTar()
    return (
        <label className="block text-sm font-medium text-foreground">
            {t(label)}
            {required && <span className="text-error ms-1">*</span>}
        </label>
    )
}

export const OrganizationDetailsSection: React.FC<SectionProps> = ({ formData, onChange, isSaving = false }) => {
    const { t } = useTar()
    const { tasks, isLoading: isLoadingTasks } = useTasks()

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <FieldLabel label="common.name" required />
                <input
                    type="text"
                    autoComplete="off"
                    value={formData.name ?? ''}
                    onChange={(e) => onChange('name', e.target.value)}
                    disabled={isSaving}
                    className={inputClasses}
                    placeholder={t('common.enterValue', { value: t('common.name').toLowerCase() })}
                />
            </div>
            <div className="space-y-2">
                <FieldLabel label="common.description" />
                <Textarea
                    value={formData.description ?? ''}
                    onChange={(e) => onChange('description', e.target.value)}
                    disabled={isSaving}
                    placeholder={t('common.enterValue', { value: t('common.description').toLowerCase() })}
                    className="min-h-[100px] resize-y"
                    autoComplete="off"
                />
            </div>
            <div className="space-y-2">
                <FieldLabel label="common.type" required />
                <SinglePick
                    options={typeOptions.map(opt => ({ label: t(opt.label), value: opt.value }))}
                    selected={formData.type}
                    onChange={(selected) => onChange('type', (selected ?? '') as PartnerService.OrganizationType)}
                    labels={{
                        placeholder: t('common.select', { value: t('common.type').toLowerCase() }),
                    }}
                />
            </div>
            <div className="space-y-2">
                <FieldLabel label="organizations.availableTasks" />
                <MultiPick
                    options={tasks.map(task => ({ label: task.name, value: task.id }))}
                    selected={formData.availableTaskIds ?? []}
                    onChange={(selected) => onChange('availableTaskIds', selected)}
                    labels={{
                        placeholder: `${t('common.select')} ${t('tasks.task').toLowerCase()}s`,
                        emptyMessage: t('tasks.noTasksMatchFilters'),
                    }}
                    disabled={isSaving || isLoadingTasks}
                />
            </div>
        </div>
    )
}

interface BrandingSectionProps extends SectionProps {
    wordmarkValidation: ValidationResult
    squareValidation: ValidationResult
}

export const OrganizationBrandingSection: React.FC<BrandingSectionProps> = ({
    formData,
    onChange,
    isSaving = false,
    wordmarkValidation,
    squareValidation,
}) => (
    <div className="space-y-4">
        <LogoUrlField
            value={formData.logoURL ?? ''}
            onValueChange={(next) => onChange('logoURL', next)}
            validation={wordmarkValidation}
            labelKey="common.logoWordmarkURL"
            helperKey="common.logoURLHelperWordmark"
            previewClassName="h-10 w-[120px]"
            isSaving={isSaving}
        />
        <LogoCanvasPicker
            value={formData.logoCanvas ?? PartnerService.OrganizationLogoCanvas.Plaque}
            onChange={(next) => onChange('logoCanvas', next)}
            logoURL={formData.logoURL ?? ''}
            orgName={formData.name ?? ''}
            isSaving={isSaving}
        />
        <LogoUrlField
            value={formData.logoSquareURL ?? ''}
            onValueChange={(next) => onChange('logoSquareURL', next)}
            validation={squareValidation}
            labelKey="common.logoSquareURL"
            helperKey="common.logoURLHelperSquare"
            previewClassName="h-16 w-16"
            isSaving={isSaving}
        />
    </div>
)

export const OrganizationSettingsSection: React.FC<SectionProps> = ({ formData, onChange, isSaving = false }) => {
    const { t } = useTar()
    const setSetting = (patch: Partial<PartnerService.OrganizationSettings>) =>
        onChange('settings', { ...formData.settings, ...patch })

    return (
        <div className="space-y-6">
            <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
                    {t('organizations.adminSettings')}
                </h3>
                <div className="flex items-center gap-4">
                    <Switch
                        checked={formData.adminSettings?.allowPublicLinks ?? false}
                        onCheckedChange={(checked) => onChange('adminSettings', {
                            ...formData.adminSettings,
                            allowPublicLinks: checked,
                        })}
                        disabled={isSaving}
                    />
                    <span className="text-sm font-medium text-foreground">
                        {t('organizations.allowPublicLinks')}
                    </span>
                </div>
            </div>

            <div className="space-y-4">
                <div className="flex items-baseline gap-2 border-b border-border pb-2">
                    <h3 className="text-lg font-medium text-foreground">
                        {t('common.settings')}
                    </h3>
                    <span className="text-sm text-muted-foreground">
                        ({t('partner.managedByPartners')})
                    </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-6 items-start justify-between">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-4">
                            <Switch
                                id="invitationEmails"
                                checked={formData.settings?.invitationEmails ?? false}
                                onCheckedChange={(checked) => setSetting({ invitationEmails: checked })}
                                disabled={isSaving}
                            />
                            <span className="text-sm font-medium text-foreground">
                                {t('organizationSettings.invitationEmails')}
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            <Switch
                                id="submissionEmails"
                                checked={formData.settings?.submissionEmails ?? false}
                                onCheckedChange={(checked) => setSetting({ submissionEmails: checked })}
                                disabled={isSaving}
                            />
                            <span className="text-sm font-medium text-foreground">
                                {t('organizationSettings.submissionNotifications')}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                        <span className="text-sm font-medium text-foreground">
                            {t('organizationSettings.feedbackLanguage')}
                        </span>
                        <LanguagePicker
                            value={formData.settings?.feedbackLanguage as FeedbackLanguage | undefined}
                            onValueChange={(v) =>
                                setSetting({ feedbackLanguage: v as PartnerService.OrganizationSettingsFeedbackLanguageEnum })
                            }
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
